import { createLogger } from '@/shared/lib/logging/logger';

import type {
  LeagueDivision,
  LeagueDivisionId,
  LeagueRankings,
  LeagueSport,
  LeagueSportId,
  LeagueTeamNotRated,
  LeagueTeamProfile,
  LeagueTeamRedirect,
  LeagueUnavailable,
} from '../model/types';

/**
 * Public League data for the League pages' getStaticProps / getStaticPaths. The source is the server-only env
 * `LEAGUE_DATA_SOURCE`: `api` (default) reads the rating-service and tournament-service (league-api.ts, `isDemo:
 * false`), `demo` keeps the fictional fixtures (league-demo.ts, `isDemo: true`). Both are loaded lazily, so client
 * bundles that only need `LEAGUE_SPORTS` or the helpers never ship the HTTP clients or the fixtures.
 *
 * Results: `null` is «no such page» (404 upstream or a sport that is not launched); `LeagueTeamNotRated` is a team
 * profile without a place in the tables yet, a 404 that is cached only briefly. Upstream failures (5xx, timeouts,
 * network) throw at runtime, so ISR keeps serving the last good page; during `next build`, which runs before or
 * independently of backend deploys, they turn into `LeagueUnavailable` (team slugs into `[]`) instead of failing
 * the build. Whether a runtime failure may throw is the page's decision: a page without a render to fall back on
 * (rendered on demand for the first time) turns it into `LeagueUnavailable` itself (`loadLeaguePageData`,
 * src/features/league/lib/page-data.ts) rather than answer HTTP 500.
 *
 * What depends on the source:
 * - robots: every League page passes `getLeagueRobots(isDemo)` (src/features/league/lib/seo.ts) to PageSEO, so API
 *   pages are indexable and demo pages are not. The «temporarily unavailable» page (`LeagueUnavailablePage`) and
 *   thin team profiles (`isTeamProfileIndexable`, src/features/league/team/lib/team-page.ts) are `noindex` either way;
 *   so are a division without teams and the rankings while no division has a team (`getLeagueDivisionRobots`,
 *   `getLeagueRankingsRobots`), whose links are still followed.
 * - sitemap: /league-sitemap.xml uses shared/config/public-content-sitemap.js at request time to load rating URLs
 *   (rankings, divisions, team slugs), only when `LEAGUE_DATA_SOURCE` is not `demo`: divisions only with teams, the rankings only
 *   while some division has them, and team URLs only for profiles that pass the same rule as
 *   `isTeamProfileIndexable`. The `/league` globs in next-sitemap.config.js EXCLUDED_PATHS keep build-output pages (demo,
 *   unavailable, thin profiles, the `/league` redirect) out, so League URLs come only from the rating.
 */

/**
 * ISR window for every League page, including `notFound` results: a sport, division or team that appears in the
 * data source later must not stay a cached 404.
 */
export const LEAGUE_REVALIDATE_SECONDS = 300;

/**
 * ISR window of a page rendered as «temporarily unavailable» because the League services were unreachable: by the
 * build, or on demand for the first time.
 */
export const LEAGUE_UNAVAILABLE_REVALIDATE_SECONDS = 30;

/**
 * ISR window of the 404 for a team profile without a place in the tables: a finished tournament links its teams to
 * profiles before the rating applies it, and a click in those moments must not keep the team page a 404 for long.
 */
export const LEAGUE_TEAM_NOT_RATED_REVALIDATE_SECONDS = 30;

export const LEAGUE_SPORTS: readonly LeagueSport[] = [
  { id: 'volleyball', launched: true },
  { id: 'football', launched: false },
  { id: 'basketball', launched: false },
];

export type LeagueDataSource = 'api' | 'demo';

/**
 * A page rendered as «temporarily unavailable»: during the build here, at runtime by the pages themselves
 * (`loadLeaguePageData`, src/features/league/lib/page-data.ts).
 */
export const LEAGUE_UNAVAILABLE: LeagueUnavailable = { kind: 'unavailable' };

const logger = createLogger('entities/league/api/league-public');

/** Read on every call rather than at import, so the build, ISR regenerations and tests each see their own env. */
export const getLeagueDataSource = (): LeagueDataSource => (process.env.LEAGUE_DATA_SOURCE === 'demo' ? 'demo' : 'api');

export const isLeagueUnavailable = (value: unknown): value is LeagueUnavailable =>
  typeof value === 'object' && value !== null && (value as Partial<LeagueUnavailable>).kind === 'unavailable';

export const isLeagueTeamRedirect = (value: unknown): value is LeagueTeamRedirect =>
  typeof value === 'object' && value !== null && (value as Partial<LeagueTeamRedirect>).kind === 'redirect';

export const isLeagueTeamNotRated = (value: unknown): value is LeagueTeamNotRated =>
  typeof value === 'object' && value !== null && (value as Partial<LeagueTeamNotRated>).kind === 'not-rated';

export const parseLeagueSportId = (value: unknown): LeagueSportId | null =>
  LEAGUE_SPORTS.find((sport) => sport.id === value)?.id ?? null;

export const isLeagueSportLaunched = (sport: LeagueSportId): boolean =>
  LEAGUE_SPORTS.some((candidate) => candidate.id === sport && candidate.launched);

/** `next build` is prerendering pages: Next refuses redirects then, and upstream failures must not fail the build. */
export const isLeagueBuildPhase = (): boolean => process.env.NEXT_PHASE === 'phase-production-build';

const loadDemo = () => import('./league-demo');

const loadFromApi = async <T>(
  load: (api: typeof import('./league-api')) => Promise<T>,
): Promise<T | LeagueUnavailable> => {
  try {
    return await load(await import('./league-api'));
  } catch (error) {
    if (!isLeagueBuildPhase()) throw error;

    logger.warn('League data is unavailable during the build, the page is left to ISR', { error });

    return LEAGUE_UNAVAILABLE;
  }
};

export const getLeagueRankings = async (sport: LeagueSportId): Promise<LeagueRankings | LeagueUnavailable | null> => {
  if (!isLeagueSportLaunched(sport)) return null;

  if (getLeagueDataSource() === 'demo') {
    return (await loadDemo()).getDemoLeagueRankings(sport);
  }

  return loadFromApi((api) => api.fetchLeagueRankings(sport));
};

export const getLeagueDivision = async (
  sport: LeagueSportId,
  divisionId: LeagueDivisionId,
): Promise<{ rankings: LeagueRankings; division: LeagueDivision } | LeagueUnavailable | null> => {
  if (!isLeagueSportLaunched(sport)) return null;

  if (getLeagueDataSource() === 'demo') {
    return (await loadDemo()).getDemoLeagueDivision(sport, divisionId);
  }

  return loadFromApi((api) => api.fetchLeagueDivision(sport, divisionId));
};

/**
 * A merged team resolves to a `LeagueTeamRedirect` to the profile it was merged into, a profile without a place in
 * the tables to `LeagueTeamNotRated` (API only: every demo team has a place).
 */
export const getLeagueTeam = async (
  sport: LeagueSportId,
  slug: string,
): Promise<LeagueTeamProfile | LeagueTeamRedirect | LeagueTeamNotRated | LeagueUnavailable | null> => {
  if (!isLeagueSportLaunched(sport)) return null;

  if (getLeagueDataSource() === 'demo') {
    return (await loadDemo()).getDemoLeagueTeam(sport, slug);
  }

  return loadFromApi((api) => api.fetchLeagueTeam(sport, slug));
};

/** Team slugs to prerender; empty when the build cannot reach the rating (the pages then render on demand). */
export const getLeagueTeamSlugs = async (sport: LeagueSportId): Promise<string[]> => {
  if (!isLeagueSportLaunched(sport)) return [];

  if (getLeagueDataSource() === 'demo') {
    return (await loadDemo()).getDemoLeagueTeamSlugs(sport);
  }

  const slugs = await loadFromApi((api) => api.fetchLeagueTeamSlugs(sport));

  return isLeagueUnavailable(slugs) ? [] : slugs;
};
